import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  Container,
  Grid,
  FormControl,
  Snackbar,
  Alert,
  Tooltip,
  Box,
  useTheme,
  IconButton,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  CircularProgress,
} from "@mui/material";
import { DropzoneArea } from "material-ui-dropzone";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LeftNavigationBar from "../../navbars/LeftNavigationBar";
import {
  clearUpdateStatus,
  getReviewById,
  updateReview,
} from "../../redux/slices/review/review";
import { useCookies } from "react-cookie";
import { HelpOutline, Clear as ClearIcon } from "@mui/icons-material";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  form: {
    width: "70%",
    marginTop: theme.spacing(1),
  },
  submit: {
    marginTop: theme.spacing(3),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: "fit-content",
    marginLeft: "auto",
    marginRight: "auto",
    display: "block",
    position: "relative",
  },
  mediaContainer: {
    position: "relative",
    marginBottom: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  profileImage: {
    maxWidth: "200px",
    maxHeight: "200px",
    marginTop: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  video: {
    maxWidth: "100%",
    marginTop: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  removeButton: {
    position: "absolute",
    top: 0,
    right: 0,
  },
  typeSelector: {
    marginBottom: theme.spacing(2),
  },
}));

const ReviewsEditForm = () => {
  const classes = useStyles();
  const { reviewId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cookies] = useCookies(["token"]);

  // Fixed: Get the correct state properties
  const { 
    updateSuccess, 
    successMessage, 
    error
  } = useSelector((state) => state.reviews);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [name, setName] = useState("");
  const [review, setDescription] = useState("");
  const [profile, setProfile] = useState(null);
  const [ratings, setRatings] = useState("");
  const [type, setType] = useState("institution");
  const [role, setRole] = useState("");
  const [institution, setInstitution] = useState("");
  const [company, setCompany] = useState("");
  const [video, setVideo] = useState(null);
  const [existingProfile, setExistingProfile] = useState("");
  const [profilePreview, setProfilePreview] = useState(null);
  const [existingVideo, setExistingVideo] = useState("");
  const [videoPreview, setVideoPreview] = useState(null);

  // Validation
  const [errors, setErrors] = useState({
    name: "",
    review: "",
    ratings: "",
    role: "",
    institution: "",
    company: "",
    video: "",
  });

  useEffect(() => {
    if (reviewId) {
      dispatch(getReviewById(reviewId))
        .then((response) => {
          const data = response.payload;
          if (data) {
            setName(data.name || "");
            setDescription(data.review || "");
            setRatings(data.ratings?.toString() || ""); // Ensure string conversion
            setRole(data.role || "");
            setType(data.type || "institution");
            setInstitution(data.institution || "");
            setCompany(data.company || "");

            // Handle profile image URL
            if (data.profile) {
              const fullProfileUrl = data.profile.startsWith("http")
                ? data.profile
                : `${process.env.REACT_APP_BASE_URL}/${data.profile}`;
              setExistingProfile(fullProfileUrl);
            }

            // Handle video URL
            if (data.video) {
              const fullVideoUrl = data.video.startsWith("http")
                ? data.video
                : `${process.env.REACT_APP_BASE_URL}/${data.video}`;
              setExistingVideo(fullVideoUrl);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching Review:", error);
          setSnackbarMessage("Error fetching review data");
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
        });
    }
  }, [reviewId, dispatch]);

  useEffect(() => {
    if (updateSuccess) {
      // Handle different message formats from the API
      let message = "Review updated successfully";
      
      if (successMessage) {
        if (typeof successMessage === "string") {
          message = successMessage;
        } else if (Array.isArray(successMessage) && successMessage.length > 0) {
          // Handle array format like [{key: "success", value: "message"}]
          message = successMessage[0]?.value || successMessage[0] || message;
        } else if (typeof successMessage === "object" && successMessage.value) {
          // Handle object format like {key: "success", value: "message"}
          message = successMessage.value;
        }
      }
      
      setSnackbarMessage(message);
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      
      setTimeout(() => {
        dispatch(clearUpdateStatus());
        navigate("/Review-control");
      }, 2000);
    }
  }, [updateSuccess, successMessage, dispatch, navigate]);

  // Handle error state
  useEffect(() => {
    if (error && !updateSuccess) {
      // Handle different error formats
      let errorMessage = "Error updating review";
      
      if (typeof error === "string") {
        errorMessage = error;
      } else if (typeof error === "object" && error.message) {
        errorMessage = error.message;
      }
      
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setIsSubmitting(false);
    }
  }, [error, updateSuccess]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    switch (name) {
      case "name":
        setName(value);
        if (!/^[a-zA-Z\s]*$/.test(value)) {
          setErrors((prev) => ({
            ...prev,
            name: "Name must contain only letters",
          }));
        } else {
          setErrors((prev) => ({ ...prev, name: "" }));
        }
        break;
      case "review":
        setDescription(value);
        setErrors((prev) => ({ ...prev, review: "" }));
        break;
      case "ratings":
        const ratingsValue = value.toString();
        setRatings(ratingsValue);
        const numRating = parseFloat(ratingsValue);
        if (isNaN(numRating) || numRating < 0 || numRating > 5) {
          setErrors((prev) => ({
            ...prev,
            ratings: "Rating must be between 0 and 5",
          }));
        } else {
          setErrors((prev) => ({ ...prev, ratings: "" }));
        }
        break;
      case "type":
        setType(value);
        // Clear validation errors for institution/company when type changes
        setErrors((prev) => ({ 
          ...prev, 
          institution: "", 
          company: "" 
        }));
        break;
      case "role":
        setRole(value);
        setErrors((prev) => ({ ...prev, role: "" }));
        break;
      case "institution":
        setInstitution(value);
        setErrors((prev) => ({ ...prev, institution: "" }));
        break;
      case "company":
        setCompany(value);
        setErrors((prev) => ({ ...prev, company: "" }));
        break;
      default:
        break;
    }
  };

  const handleImageChange = (fileArray) => {
    if (fileArray.length > 0) {
      setProfile(fileArray[0]);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result);
      };
      reader.readAsDataURL(fileArray[0]);
    }
  };

  const handleRemoveProfile = () => {
    setExistingProfile("");
    setProfile(null);
    setProfilePreview(null);
  };

  const handleVideoChange = (fileArray) => {
    if (fileArray.length > 0) {
      const file = fileArray[0];
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes

      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          video: "Video file size must be 10MB or less",
        }));
        setVideo(null);
        return;
      }

      setVideo(file);
      setErrors((prev) => ({ ...prev, video: "" }));

      // Create video preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setVideoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveVideo = () => {
    setExistingVideo("");
    setVideo(null);
    setVideoPreview(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting) return;

    // Validation
    let hasErrors = false;
    const newErrors = { ...errors };

    if (!name.trim()) {
      newErrors.name = "Name is required";
      hasErrors = true;
    }
    
    if (!review.trim()) {
      newErrors.review = "Review is required";
      hasErrors = true;
    }
    
    const numRating = parseFloat(ratings);
    if (!ratings || isNaN(numRating) || numRating < 0 || numRating > 5) {
      newErrors.ratings = "Rating is required and must be between 0 and 5";
      hasErrors = true;
    }
    
    if (!role.trim()) {
      newErrors.role = "Role is required";
      hasErrors = true;
    }

    if (type === "institution" && !institution.trim()) {
      newErrors.institution = "Institution is required";
      hasErrors = true;
    }

    if (type === "company" && !company.trim()) {
      newErrors.company = "Company is required";
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    // Clear any previous errors
    setErrors({
      name: "",
      review: "",
      ratings: "",
      role: "",
      institution: "",
      company: "",
      video: "",
    });

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("review", review.trim());
    formData.append("ratings", ratings);
    formData.append("type", type);
    formData.append("role", role.trim());
    
    if (type === "institution") {
      formData.append("institution", institution.trim());
    } else {
      formData.append("company", company.trim());
    }

    if (profile) {
      formData.append("profile", profile);
    } else if (!existingProfile) {
      formData.append("removeProfile", "true");
    }

    if (video) {
      formData.append("video", video);
    } else if (!existingVideo) {
      formData.append("removeVideo", "true");
    }

    try {
      const result = await dispatch(
        updateReview({ reviewId, formData, token: cookies.token })
      );
      
      // Check if the action was rejected
      if (updateReview.rejected.match(result)) {
        throw new Error(result.payload || result.error?.message || "Update failed");
      }
      
    } catch (error) {
      console.error("Error updating review:", error);
      setSnackbarMessage(error.message || "Error updating review");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <LeftNavigationBar
      Content={
        <Container component="main" maxWidth="md">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            mt={2}
            mb={2}
          >
            <Button variant="outlined" color="primary" onClick={handleBack}>
              Back
            </Button>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                flex: 1,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  position: "relative",
                  padding: 0,
                  margin: 0,
                  fontWeight: 300,
                  fontSize: { xs: "32px", sm: "40px" },
                  color: "#747474",
                  textAlign: "center",
                  textTransform: "uppercase",
                  paddingBottom: "5px",
                  "&::before": {
                    content: '""',
                    width: "28px",
                    height: "5px",
                    display: "block",
                    position: "absolute",
                    bottom: "3px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "#747474",
                  },
                  "&::after": {
                    content: '""',
                    width: "100px",
                    height: "1px",
                    display: "block",
                    position: "relative",
                    marginTop: "5px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "#747474",
                  },
                }}
              >
                Testimonial Edit Form
              </Typography>
              <Tooltip
                title="This is where you can edit testimonial information."
                arrow
              >
                <HelpOutline
                  sx={{
                    color: "#747474",
                    fontSize: "24px",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            </Box>
          </Box>

          <Snackbar
            open={openSnackbar}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity={snackbarSeverity} variant="filled" onClose={handleCloseSnackbar}>
              {snackbarMessage}
            </Alert>
          </Snackbar>

          <form
            onSubmit={handleSubmit}
            style={{
              border: "2px dotted #D3D3D3",
              padding: "20px",
              marginTop: "20px",
              borderRadius: "8px",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl component="fieldset" className={classes.typeSelector}>
                  <FormLabel component="legend">Testimonial Type</FormLabel>
                  <RadioGroup
                    row
                    name="type"
                    value={type}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="institution"
                      control={<Radio />}
                      label="Institution"
                    />
                    <FormControlLabel
                      value="company"
                      control={<Radio />}
                      label="Company"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  variant="outlined"
                  required
                  value={name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Role"
                  name="role"
                  variant="outlined"
                  required
                  value={role}
                  onChange={handleChange}
                  error={!!errors.role}
                  helperText={errors.role}
                  disabled={isSubmitting}
                />
              </Grid>

              {type === "institution" ? (
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Institution"
                    name="institution"
                    variant="outlined"
                    required
                    value={institution}
                    onChange={handleChange}
                    error={!!errors.institution}
                    helperText={errors.institution}
                    disabled={isSubmitting}
                  />
                </Grid>
              ) : (
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Company"
                    name="company"
                    variant="outlined"
                    required
                    value={company}
                    onChange={handleChange}
                    error={!!errors.company}
                    helperText={errors.company}
                    disabled={isSubmitting}
                  />
                </Grid>
              )}

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Ratings"
                  name="ratings"
                  variant="outlined"
                  required
                  type="number"
                  value={ratings}
                  onChange={handleChange}
                  error={!!errors.ratings}
                  helperText={errors.ratings}
                  disabled={isSubmitting}
                  inputProps={{
                    step: "0.1",
                    min: "0",
                    max: "5",
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Review"
                  name="review"
                  variant="outlined"
                  required
                  multiline
                  rows={4}
                  value={review}
                  onChange={handleChange}
                  error={!!errors.review}
                  helperText={errors.review}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12}>
                {(existingProfile || profilePreview) && (
                  <div className={classes.mediaContainer}>
                    <Typography variant="subtitle1">Current Profile Image:</Typography>
                    <img
                      src={profilePreview || existingProfile}
                      alt="Profile"
                      className={classes.profileImage}
                    />
                    <IconButton
                      className={classes.removeButton}
                      onClick={handleRemoveProfile}
                      color="secondary"
                      disabled={isSubmitting}
                    >
                      <ClearIcon />
                    </IconButton>
                  </div>
                )}

                <div style={{ marginTop: "16px" }}>
                  <DropzoneArea
                    onChange={handleImageChange}
                    acceptedFiles={[
                      "image/png",
                      "image/svg+xml",
                      "image/jpeg",
                      "image/jpg",
                      "image/*",
                    ]}
                    filesLimit={1}
                    showPreviews={false}
                    showPreviewsInDropzone={true}
                    dropzoneText="Drag and drop a profile image (PNG, SVG, JPG, JPEG) here or click"
                    disabled={isSubmitting}
                  />
                </div>
              </Grid>

              <Grid item xs={12}>
                {(existingVideo || videoPreview) && (
                  <div className={classes.mediaContainer}>
                    <Typography variant="subtitle1">Current Video:</Typography>
                    <video
                      controls
                      className={classes.video}
                      src={videoPreview || existingVideo}
                    >
                      Your browser does not support the video tag.
                    </video>
                    <IconButton
                      className={classes.removeButton}
                      onClick={handleRemoveVideo}
                      color="secondary"
                      disabled={isSubmitting}
                    >
                      <ClearIcon />
                    </IconButton>
                  </div>
                )}

                <div style={{ marginTop: "16px" }}>
                  <DropzoneArea
                    onChange={handleVideoChange}
                    acceptedFiles={["video/*"]}
                    filesLimit={1}
                    maxFileSize={10 * 1024 * 1024}
                    showPreviews={false}
                    showPreviewsInDropzone={true}
                    dropzoneText="Upload a Video (Max: 10MB)"
                    disabled={isSubmitting}
                  />
                  {errors.video && (
                    <Typography color="error" variant="caption">
                      {errors.video}
                    </Typography>
                  )}
                </div>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                color="warning"
                type="submit"
                className={classes.submit}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isSubmitting ? "Updating..." : "Update Testimonial Data"}
              </Button>
            </Box>
          </form>
        </Container>
      }
    />
  );
};

export default ReviewsEditForm;